/***********************/
/* Angular + external */
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChildren,
} from "@angular/core";
import { LoggerService } from "@bfx/logger";

import { CustomField, CustomValue, CustomValueImpl } from "shared/cubiq-model-multi-asset";
import { OENOperationMode } from "src/app/order-entry/data/enums/oe-operation-mode.enum";
import {
  CustomValueUpdate,
  ICCustomFieldInstanceComponent,
} from "src/app/order-entry/interface/controls/ic-custom-field-instance.component";
/***********************/
import { OEDatasetService } from "src/app/order-entry/services/basic/oe-dataset.service";
import { ICNInputControlState } from "./models/bfx-interface-controls.enums";

const logger = LoggerService.for(__dirname);

// models

type CustomFieldInstanceData = {
  defn: CustomField;
  value: string;
  externalState: ICNInputControlState;
};

/**
 * ICCustomFieldsComponent
 * -----------------------
 * Component that takes a list of CustomVAriableDefn instances and Custom Values and creates a set
 * of form controls that allow these values to be displayed, changed and validated.
 */
@Component({
  selector: "ic-custom-fields",
  templateUrl: "./html/ic-custom-fields.html",
  standalone: false,
})
export class ICCustomFieldsComponent implements OnInit, OnDestroy {
  /** Are all field instances disabled?  */
  public isDisabled: boolean = false;

  /** list of all editable fields that are to be displayed in the component */
  public editableFields: Array<CustomFieldInstanceData> = [];

  /** List of visible fields */
  private _visibleFields: Array<CustomField> = [];

  /** List of initial custom values */
  private _customValues: Array<CustomValue> = [];

  /** Array of values always used as value payload for all change events */
  private _allValues: Array<CustomValue> = [];

  /** Map used to quickly find the value model in the array for a given field ID */
  private _allValuesMap: Map<string, CustomValue> = new Map();

  /** Pushed control state that causes validation of components when INVALID */
  private _externalState: ICNInputControlState = null;

  /**
   *Used to determine whether to support copying of defaults for empty values
   */
  @Input() public operationMode: OENOperationMode = OENOperationMode.NEW;

  /**
   * Emitter dispatching entire array of custom values on change - note the CustomValue members
   * should be treated as immutable
   */
  @Output()
  public valuesChanged: EventEmitter<Array<CustomValue>> = new EventEmitter();

  @ViewChildren(ICCustomFieldInstanceComponent)
  private customFieldInstances: QueryList<ICCustomFieldInstanceComponent>;

  /**
   * Used to determine whether to support custom fields that are disabled on modify when any strategy order  has a fill
   */
  public hasStrategyFill: boolean = false;

  constructor(private datasetService: OEDatasetService) {}

  /**********************************************************************/
  /* LIFE CYCLE HOOKS */
  ngOnInit() {
    this.initialize();
  }

  ngOnDestroy() {
    this.valuesChanged.complete();
  }

  /**********************************************************************/
  /* INITIALIZATION */
  /**
   * Initialise all fields and values, populating editableFields and _allValues
   */
  public initialize(): void {
    const defns = this.datasetService.customFieldDefns.editable;
    this._visibleFields = defns;

    this.initCustomFields();
  }

  /**
   * Initialize the custom fields, figuring out which are editable. For those which aren't editable
   * we still need to be able to pass back the values in an array as they need to be saved
   */
  private initCustomFields(): void {
    const editableFields: Array<CustomFieldInstanceData> = [];
    const allValues: Array<CustomValue> = [];
    const allValuesMap: Map<string, CustomValue> = new Map();

    const valueMap = new Map<string, CustomValue>();

    if (this._customValues) {
      for (let value of this._customValues) {
        valueMap.set(value.fieldId, value);
      }
    }

    let defaulted = false;

    this._visibleFields.forEach((field, i) => {
      let customValue = valueMap.get(field.fieldId);
      if (!customValue) {
        customValue = new CustomValueImpl();
        customValue.fieldId = field.fieldId;
        // For modify/duplicate etc we do not want to auto populate any empty values
        if (this.operationMode == OENOperationMode.NEW) {
          customValue.value = field.defaultValueId;
          defaulted = true;
        }
      } else {
        valueMap.delete(field.fieldId);
      }
      allValues.push(customValue);
      allValuesMap.set(customValue.fieldId, customValue);

      const valueStr = customValue.value;
      editableFields.push({
        defn: field,
        value: valueStr,
        externalState: this.externalState,
      });
    });

    for (let [_, customValue] of valueMap) {
      // This value is not editable so let's just remember it
      allValues.push(customValue);
      allValuesMap.set(customValue.fieldId, customValue);
    }

    this._allValues = allValues;
    this._allValuesMap = allValuesMap;
    this.editableFields = editableFields;

    // We've changed at least one of the values so need to notify of value changes
    if (defaulted) {
      this.valuesChanged.emit(this._allValues.concat());
    }
  }

  /**********************************************************************/
  /* BASIC - API */

  @Input()
  public set externalState(value: ICNInputControlState) {
    this._externalState = value;
  }

  public get externalState(): ICNInputControlState {
    return this._externalState;
  }

  public resetInputControl(): void {
    this.unlockInputControl();
    this._customValues = [];
    this.initCustomFields();
  }

  public lockInputControl(): void {
    this.isDisabled = true;
  }

  public unlockInputControl(): void {
    this.isDisabled = false;
  }

  public loadCustomValues(values: Array<CustomValue>, hasStrategyFill: boolean): void {
    this.hasStrategyFill = hasStrategyFill;
    this._customValues = values;
    this.initCustomFields();
  }

  public get hasCustomFields(): boolean {
    return this._visibleFields && this._visibleFields.length > 0;
  }

  public get value(): Array<CustomValue> {
    return this._allValues.concat();
  }

  public onValueChange(event: CustomValueUpdate): void {
    let customValue = this._allValuesMap.get(event.defn.fieldId);
    if (customValue && customValue.value !== event.value.value) {
      customValue.value = event.value.value;
      this.valuesChanged.emit(this._allValues.concat());
      this.externalState = null;
    }
  }
}
